import AnalysisLensHelperText from '@components/common/AnalysisLensHelperText'; // Import Helper Text
import DoNotDisturbOnOutlinedIcon from '@mui/icons-material/DoNotDisturbOnOutlined'; // Other reaction
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline'; // Missed/Indifferent
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined'; // Resonant
import {
  Box,
  Card,
  CardContent,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Stack,
  Typography,
} from '@mui/material';

// Helper function to get reaction icon and color
const getReactionStyle = (reactionType) => {
  switch (reactionType?.toLowerCase()) {
    case 'affirming':
      return { icon: <ThumbUpAltOutlinedIcon fontSize="small" />, color: 'success' };
    case 'cognitive':
      return { icon: <ThumbUpAltOutlinedIcon fontSize="small" />, color: 'info' };
    case 'indifferent':
      return { icon: <HelpOutlineIcon fontSize="small" />, color: 'default' };
    case 'skeptical':
      return { icon: <DoNotDisturbOnOutlinedIcon fontSize="small" />, color: 'warning' };
    case 'resistant':
      return { icon: <DoNotDisturbOnOutlinedIcon fontSize="small" />, color: 'error' };
    case 'other':
    default:
      return { icon: <HelpOutlineIcon fontSize="small" />, color: 'secondary' };
  }
};

const ResonanceScanAnalysisTranscript = ({ summaryData }) => {
  if (!summaryData?.results) {
    return (
      <Card variant="outlined" sx={{ p: 0, width: '100%', marginTop: '20px' }}>
        <Box p={2} display="flex" justifyContent="center">
          Resonance Scan analysis data not available.
        </Box>
      </Card>
    );
  }

  const { results } = summaryData;
  const { summary, resonant_messages = [], missed_messages = [] } = results;

  const renderMessageItem = (item, index, isResonant = true) => {
    const { message, reaction_type, participant_response, quotes } = item;
    const { icon, color } = getReactionStyle(reaction_type);
    const IconComponent = isResonant ? ThumbUpAltOutlinedIcon : HelpOutlineIcon; // Default icon for section

    return (
      <Box
        key={`${isResonant ? 'res' : 'miss'}-${index}`}
        sx={{
          mb: 3,
          p: 2,
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          bgcolor: 'background.paper',
        }}
      >
        <Stack spacing={1.5}>
          <Typography
            variant="subtitle1"
            sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 'medium' }}
          >
            <IconComponent color={isResonant ? 'success' : 'action'} />
            {message}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Reaction:
            </Typography>
            <Chip
              icon={icon}
              label={reaction_type || 'N/A'}
              color={color}
              size="small"
              variant="outlined"
            />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Participant Response:
            </Typography>
            <Typography variant="body2" sx={{ pl: 1 }}>
              {participant_response || 'N/A'}
            </Typography>
          </Box>

          {quotes?.length > 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Illustrative Quotes:
              </Typography>
              <List dense disablePadding sx={{ pl: 1 }}>
                {quotes.map((quote, idx) => (
                  <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 24, mt: 0.5 }}>
                      <FormatQuoteIcon
                        fontSize="small"
                        sx={{ transform: 'scaleX(-1)' }}
                        color="disabled"
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={quote}
                      primaryTypographyProps={{ variant: 'body2', fontStyle: 'italic' }}
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  return (
    <Card variant="outlined" sx={{ width: '100%', marginTop: '20px', overflow: 'hidden', p: 0 }}>
      {/* Header */}
      <Box px={2} py={1.5} bgcolor="grey.100" borderBottom={1} borderColor="divider">
        <Typography
          variant="h6"
          component="div"
          sx={{ fontWeight: 'bold', display: 'inline-flex', alignItems: 'center' }}
        >
          Resonance Scan Analysis{' '}
          <AnalysisLensHelperText lensName="resonance_scan" level="transcript" size="small" />
        </Typography>
      </Box>

      <CardContent sx={{ p: { xs: 1.5, md: 3 } }}>
        {/* Overall Summary */}
        {summary && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="subtitle1" color="text.secondary" gutterBottom>
              Overall Summary
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {summary}
            </Typography>
          </Box>
        )}

        {/* Resonant Messages */}
        {resonant_messages.length > 0 && (
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Resonant Messages
            </Typography>
            {resonant_messages.map((item, index) => renderMessageItem(item, index, true))}
          </Box>
        )}

        {/* Missed Messages */}
        {missed_messages.length > 0 && (
          <Box>
            <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
              Missed or Neglected Messages
            </Typography>
            {missed_messages.map((item, index) => renderMessageItem(item, index, false))}
          </Box>
        )}

        {/* Handle case where no messages are present */}
        {resonant_messages.length === 0 && missed_messages.length === 0 && (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', mt: 2 }}>
            No specific resonant or missed messages identified in this analysis.
          </Typography>
        )}

        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          To view the overall analysis that created this artifact go to &apos;Analysis Results&apos;
          tab
        </Typography>
      </CardContent>
    </Card>
  );
};

export default ResonanceScanAnalysisTranscript;
